import React, {useState, useCallback} from 'react'
import {useLocation} from 'react-router-dom'
import {BlobUpload, Problem} from 'data/types'
import services from 'services'
import Portal from 'components/Portal'
import {Optional} from 'data/types'
import {useFunction} from 'utils/hooks/function'
import Button from 'components/Button'
import Dropzone from 'components/Dropzone'
import EditableText from 'components/EditableText'
import {useForm} from 'utils/hooks/form'
import i18n from 'i18n'
import cross from 'assets/cross.svg'
import {readFile} from 'utils'
import api from 'services/APIRequestor'
import {Col, Row} from 'components/Display'
import {AutoSizer} from 'react-virtualized'
import getImageSrcFromBlob from 'utils/blob'

type AddSolutionView = {
  problem: Problem
}

const fetchSolutionSuggestion = async (problemId: number) => {
  const result = await api.get<any>(`/problems/${problemId}/suggested_solution`)
  if (result.ok) {
    const solution: string = result.body.solutionSuggestion
    return solution
  }
  return null
}

export default function AddSolutionView(props: AddSolutionView) {
  const [isOpen, setOpen] = useState(false)
  const formData = useForm({
    title: '',
    description: '',
    isEditingTitle: true,
    isEditingDescription: true,
    suggestedSolution: null as Optional<string>,
    images: null as Optional<string>,
    blob: null as Optional<BlobUpload>,
  })

  const problemSolutionService = services.getSingleProblemService(props.problem.id)
  const submitSolution = useFunction(problemSolutionService.createSolution)

  const location = useLocation()
  const inSolutionPage = location.pathname.includes('solutions')

  const onDrop = useCallback(
    (acceptedFiles) => {
      // this callback will be called after files get dropped, we will get the acceptedFiles. If you want, you can even access the rejected files too
      // Loop through accepted files
      acceptedFiles.map((file: any) => readFile(file).then((fileContent) => formData.update('blob', fileContent)))
    },
    [formData]
  )

  return (
    <React.Fragment>
      <Row>
        <Button
          onClick={() => services.router.selectSolutions(props.problem)}
          style={{fontWeight: 500, paddingLeft: '4px', paddingRight: '4px'}}
          className={`py-1 mr-2 bg-white border-itx-greyscale-3 text-itx-greyscale-3 ${inSolutionPage === true && 'hidden'}`}
          content={i18n.t('solutions.getProposal')}
        />
        <Button
          onClick={() => {
            setOpen(true)
            formData.update('suggestedSolution', null)
            fetchSolutionSuggestion(props.problem.id).then((solution) => {
              formData.update('suggestedSolution', solution)
            })
          }}
          className="py-1 bg-itx-primary-4 hover:bg-itx-primary-3 text-white"
          content={i18n.t('solutions.proposeSolution')}
        />
      </Row>

      {isOpen && (
        <Portal>
          <div className="flex flex-col items-center problem-solution-modal justify-center absolute top-0 left-0 w-full h-full">
            <div className="flex flex-col justify-between w-6/12 h-2/3 problem-solution-modal-content shadow-lg bg-white rounded-lg">
              <div className="flex flex-row justify-between border-b border-gray-itx-border pl-8 pr-6 py-6">
                <div className="text-base font-semibold text-gray-itx3">{i18n.t('solutions.newSolution')}</div>
                <img className={`w-4 h-4 cursor-pointer`} alt={'cross'} src={cross} onClick={() => setOpen(false)} />
              </div>
              <div className="flex flex-1 flex-col px-8 py-2">
                <EditableText
                  placeholder={i18n.t('solutions.solutionTitle')}
                  value={formData.state.title}
                  onChange={(title) => formData.update('title', title)}
                  editable={formData.state.isEditingTitle}
                  updateEditMode={(mode) => formData.update('isEditingTitle', mode)}
                  autoFocus
                  className="text-xl font-medium font-sans text-primary-darkest placeholder-itx-gray-base mt-2 w-full"
                />
                {formData.state.suggestedSolution != null && (
                  <Row className="pb-4 text-sm text-gray-500">Suggested: {formData.state.suggestedSolution}</Row>
                )}
                <div className="mt-4" />
                {/* // We pass onDrop function and accept prop to the component. It will be used as initial params for useDropzone hook */}
                <Dropzone
                  onDrop={onDrop}
                  accept={['image/jpeg', 'image/png']}
                  blob={formData.state.blob}
                  dragRejectText={i18n.t('problems.dragRejectText')}
                  dragAcceptText={i18n.t('problems.dragAcceptText')}
                  releaseToDropText={i18n.t('problems.releaseToDropText')}
                >
                  {(blob) => {
                    return (
                      <Col>
                        <img alt="addProblemImage" className="w-full object-cover" style={{maxHeight: '212px'}} src={getImageSrcFromBlob(blob)} />
                        <AutoSizer className="absolute">
                          {(size) => {
                            return (
                              <Row style={size} className="justify-center items-center">
                                <Button className="text-white bg-itx-primary-3 border-itx-primary-3" content="Switch image" />
                              </Row>
                            )
                          }}
                        </AutoSizer>
                      </Col>
                    )
                  }}
                </Dropzone>

                <EditableText
                  placeholder={i18n.t('solutions.writeDescription')}
                  value={formData.state.description}
                  onChange={(desc) => formData.update('description', desc)}
                  editable={formData.state.isEditingDescription}
                  updateEditMode={(mode) => formData.update('isEditingDescription', mode)}
                  className="font-medium font-sans text-primary-darkest placeholder-itx-gray-base mt-4 outline-none py-4 resize-none "
                  isParagraph
                />
              </div>
              <div className="flex flex-row border-t border-gray-300 pt-2 px-2 pb-2 justify-end">
                <Button
                  className="my-4 text-gray-itx border-2 border-gray-300 rounded py-2"
                  content={i18n.t('misc.cancel')}
                  onClick={() => setOpen(false)}
                />
                <Button
                  loading={submitSolution.isRunning}
                  disabled={!formData.state.title || submitSolution.isRunning}
                  className="my-4 mr-6 ml-2 py-2 bg-itx-primary-4 hover:bg-itx-primary-3 text-white"
                  content={i18n.t('solutions.createSolution')}
                  onClick={() =>
                    submitSolution({
                      title: formData.state.title,
                      description: formData.state.description,
                      images: formData.state.blob ? [formData.state.blob] : [],
                    }).then(() => {
                      formData.clear()
                      setOpen(false)
                    })
                  }
                />
              </div>
            </div>
          </div>
        </Portal>
      )}
    </React.Fragment>
  )
}
